/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import { connect } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import differenceBy from 'lodash/differenceBy';

import { PromptExit } from 'libs/router';
import ActionLink from 'components/buttons/action-link/ActionLink';

import DeliverableApprovalNote from './deliverable-approval-note/DeliverableApprovalNote';
import DeliverableCaptionApproval from './deliverable-caption-approval/DeliverableCaptionApproval';
import DeliverableContentForApproval from './deliverable-content-for-approval/DeliverableContentForApproval';
import DeliverableTabFooter from '../deliverable-tab-footer/DeliverableTabFooter';
import DeliverableClientApproval from '../deliverable-client-approval/DeliverableClientApproval';
import { DeliverableEntertainmentVideoForReview } from './deliverable-entertainment-video-for-review/DeliverableEntertainmentVideoForReview';
import DeliverableOutfitContentForReview from './deliverable-outfit-content-for-review/DeliverableOutfitContentForReview';
import DeliverableMoveContentAttachments from '../deliverable-move-content/DeliverableMoveContentAttachments';
import DeliverableMoveContentOutfits from '../deliverable-move-content/DeliverableMoveContentOutfits';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { downloadDeliverableAttachments } from 'modules/deliverables/reducers/deliverableMediaAttachments';
import {
  getPreviouslySavedDeliverable,
  markApprovalNoteAsRead,
  updateApprovalStatus,
  updateContentApprovalData
} from 'modules/deliverables/reducers/deliverableContentApproval';
import { deleteDeliverableOutfits, editApprovalNote, updateContentApproval } from 'modules/subcampaigns/reducers/offerDeliverables';

import { APPROVAL_STATUS_PENDING } from 'utils/profile';
import { getDisplayDateRange } from 'utils/date';
import { __t, applyTranslations } from 'utils/language';
import { DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO, DELIVERABLE_TYPE_ZALANDO_OUTFIT, hasCaptionBeenUpdated, isPersistedAttachment } from 'utils/deliverables';
import { getOutfitsWithoutDraft } from 'utils/realm-outfits';

import {
  Deliverable,
  DeliverableAttachment,
  DeliverableAttachmentApprovalStatus,
  DeliverableCaption,
  DeliverableClientApproval as DeliverableClientApprovalAlias,
  DeliverableContentApproval,
  DeliverableGenericTimeline,
  DeliverablePersistedAttachment,
  DeliverableShort,
  DeliverableUploadedEventAction,
  DeliverableWithContentApproval,
  RealmOutfit,
  Subcampaign,
  SubcampaignInfluencerProfile
} from 'types';

import './deliverable-content-approval-tab.scss';

applyTranslations(require('./deliverable-content-approval-tab.i18n.json'));

type BrandInfluencer = 'brand' | 'influencer';
type Note = { text: string, authorType: BrandInfluencer };
type ClientFeebackApplyCallback = (attachmentId?: number | null) => void;
type DataUpdateReturnType = Promise<DeliverableContentApproval & { contentApprovalTimeline: DeliverableGenericTimeline }>;

type OwnProps = {
  deliverable: DeliverableWithContentApproval,
  subcampaign: Subcampaign,
  isForBrand?: boolean,
  isCcm?: boolean,
  deliverablesWithCA2: Array<DeliverableShort>,
  contentApprovalView: 'default' | 'client-approval' | 'move-content',
  postAttachments: Array<DeliverableAttachment>,
  clientApprovalData: DeliverableClientApprovalAlias,
  getIsPublishedToClientStatus: (asset: DeliverableAttachment) => boolean,
  setDeliverableModified: (isModified: boolean) => void,
  hasClientProvidedCaptionFeedback: () => boolean,
  onContentMoved: () => void,
  onOpenMoveContent: (showMoveContent: boolean) => void,
  onOpenClientApproval: (openClientApproval?: boolean) => void,
  onTrackDeliverableUploadEvent: (eventAction: DeliverableUploadedEventAction) => void,
  onSelectMoveOutfitToAnotherDeliverable?: (outfit: RealmOutfit, checked: boolean) => void
};

type DispatchProps = {
  dispatchMarkApprovalNoteAsRead: (
    offerId: number,
    deliverableId: number,
    ids: Array<number>,
    readerType: BrandInfluencer
  ) => Promise<void>,
  dispatchUpdateContentApprovalData: (
    offerId: number,
    deliverableId: number,
    addAttachments: Array<DeliverableAttachment>,
    deleteAttachments: Array<number>,
    caption: DeliverableCaption,
    note: Note | null
  ) => DataUpdateReturnType,
  dispatchDownloadAttachments: (
    campaignId: number,
    creatorProfile: SubcampaignInfluencerProfile | undefined | null,
    deliverable: Deliverable,
    attachmentType?: string | null,
    approvedOnly?: boolean | null
  ) => void,
  dispatchShowConfirmationModal: (
    params: {
      title?: string,
      message?: string | React.ReactNode,
      confirmButtonLabel?: string,
      onConfirm?: () => void,
      onCancel?: () => void,
      onClose?: () => void,
      innerContent?: React.ReactNode
    }
  ) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchUpdateApprovalStatus: (
    offerId: number,
    deliverableId: number,
    attachments: Array<DeliverablePersistedAttachment>,
    caption?: DeliverableAttachmentApprovalStatus
  ) => Promise<DeliverableGenericTimeline | undefined>,
  dispatchEditApprovalNote: (
    offerId: number,
    deliverableId: number,
    noteId: number,
    noteValue: string
  ) => Promise<void>,
  dispatchUpdateDeliverable: (
    offerId: number,
    deliverableId: number,
    contentApproval: DeliverableContentApproval,
    contentApprovalTimeline: DeliverableGenericTimeline
  ) => void,
  dispatchGetPreviouslySavedDeliverable: (
    offerId: number,
    deliverableId: number
  ) => Promise<Deliverable>,
  dispatchDeleteOutfits: (deliverableId: number, offerId: number) => Promise<void>
};

type Props = OwnProps & DispatchProps;

type State = {
  isEditMode: boolean,
  approvalNote: string,
  approvalNoteId: null | number,
  caption: DeliverableCaption,
  attachments: Array<DeliverableAttachment>,
  isModified: boolean
};

const msgKey = 'deliverable_content_approval_tab';

class DeliverableContentApprovalTab extends React.PureComponent<Props, State> {
  constructor(props) {
    super(props);

    const { deliverable, isForBrand } = props;
    const { contentApproval } = deliverable;

    this.state = {
      isEditMode: !isForBrand,
      approvalNote: '',
      approvalNoteId: null,
      caption: contentApproval.caption,
      attachments: props.postAttachments,
      isModified: false
    };
  }

  componentDidMount() {
    const { deliverable, subcampaign, isForBrand, dispatchMarkApprovalNoteAsRead } = this.props;
    const { contentApproval, id } = deliverable;
    const { offer } = subcampaign;
    const currentAuthorType = isForBrand ? 'brand' : 'influencer';

    const newApprovalNotes = contentApproval.approvalNotes.filter(note => note.isNew && note.author.type !== currentAuthorType);

    if (newApprovalNotes.length) {
      const newApprovalNotesIds = newApprovalNotes.map(note => note.id);

      dispatchMarkApprovalNoteAsRead(offer.id, id, newApprovalNotesIds, currentAuthorType);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { postAttachments } = this.props;

    if (!isEqual(postAttachments, prevProps.postAttachments)) {
      this.setState({
        attachments: postAttachments
      });
    }
  }

  componentWillUnmount() {
    const { setDeliverableModified, contentApprovalView, onOpenClientApproval } = this.props;

    setDeliverableModified(false);
    onOpenClientApproval(contentApprovalView === 'client-approval');
  }

  handleOnTrackDeliverableUploadEvent() {
    const { postAttachments, onTrackDeliverableUploadEvent } = this.props;
    const { attachments, approvalNote } = this.state;
    const hasAttachmentsChanges = postAttachments.length !== attachments.length
      || differenceBy(postAttachments, attachments, 'id').length > 0;

    if (approvalNote.length) {
      onTrackDeliverableUploadEvent('caption for approval');
    }

    if (hasAttachmentsChanges) {
      onTrackDeliverableUploadEvent('content for approval');
    }
  }

  handleModification = (isDeliverableChanged: boolean, nextState: Partial<State>) => {
    const { setDeliverableModified } = this.props;

    this.setState(prevState => ({
      ...prevState,
      ...nextState,
      isModified: isDeliverableChanged
    }));
    setDeliverableModified(isDeliverableChanged);
  };

  showCaptionRePublishWarning = () => {
    const { deliverable, dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;
    const { isModified, caption } = this.state;
    const { contentApproval } = deliverable;

    if (!isModified) {
      return Promise.resolve();
    }

    const wasCaptionUpdated = contentApproval.caption.text !== caption.text;
    const wasCaptionApprovalStatusUpdated = contentApproval.caption.approvalStatus !== caption.approvalStatus;
    const shouldShowModal = contentApproval.caption.approvalStatus === 'approved'
      && (wasCaptionUpdated || wasCaptionApprovalStatusUpdated);

    if (!shouldShowModal) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.republish_approval_modal.title`),
        message: __t(`${msgKey}.republish_approval_modal.message`),
        confirmButtonLabel: __t(`${msgKey}.republish_approval_modal.confirm_btn`),
        innerContent: (
          <div className="deliverable-content-approval-tab__modal-confirmation">
            {__t(`${msgKey}.republish_approval_modal.confirmation`)}
          </div>
        ),
        onClose: () => {
          dispatchHideConfirmationModal();
          resolve();
        },
        onConfirm: () => {
          dispatchHideConfirmationModal();
          resolve();
        }
      });
    });
  };

  handleDispatchUpdateContentApprovalData = (
    offerId: number,
    deliverableId: number,
    addedAttachments: Array<DeliverableAttachment>,
    deletedAttachments: Array<DeliverableAttachment>,
    caption: DeliverableCaption,
    note: Note
  ): DataUpdateReturnType => {
    const { dispatchUpdateContentApprovalData } = this.props;

    return dispatchUpdateContentApprovalData(
      offerId,
      deliverableId,
      addedAttachments,
      // @ts-ignore deletedAttachments will always have ids as number, as its extracted from BE data
      deletedAttachments,
      caption,
      note
    );
  };

  triggerConfirmationModal = (
    addedAttachments: Array<DeliverableAttachment>,
    deletedAttachments: Array<DeliverableAttachment>,
    previouslySavedDeliverable: Deliverable
  ): void => {
    const {
      deliverable,
      subcampaign,
      isForBrand,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal
    } = this.props;

    const { approvalNote } = this.state;
    const { offer } = subcampaign;
    const { id } = deliverable;

    const note = {
      text: approvalNote,
      authorType: (isForBrand ? 'brand' : 'influencer') as BrandInfluencer
    };

    return dispatchShowConfirmationModal({
      title: __t(`${msgKey}.caption_approval_error_modal.title`),
      message: __t(`${msgKey}.caption_approval_error_modal.message`),
      confirmButtonLabel: __t(`${msgKey}.caption_approval_error_modal.button`),
      innerContent: (
        <div className="deliverable-content-approval-tab__modal-confirmation">
          {__t(`${msgKey}.caption_approval_error_modal.inner`)}
        </div>
      ),
      onClose: async () => {
        dispatchHideConfirmationModal();

        const updatedData = await this.handleDispatchUpdateContentApprovalData(
          offer.id,
          id,
          addedAttachments,
          deletedAttachments,
          previouslySavedDeliverable.contentApproval.caption,
          note
        );

        this.finishSaveData(updatedData);
      },
      onConfirm: async () => {
        dispatchHideConfirmationModal();

        const updatedData = await this.handleDispatchUpdateContentApprovalData(
          offer.id,
          id,
          addedAttachments,
          deletedAttachments,
          previouslySavedDeliverable.contentApproval.caption,
          note
        );

        this.finishSaveData(updatedData);
      }
    });
  };

  saveData = async () => {
    // used when sending updates possibly with new content
    const {
      deliverable,
      subcampaign,
      postAttachments,
      isForBrand,
      dispatchUpdateApprovalStatus,
      dispatchGetPreviouslySavedDeliverable
    } = this.props;

    const { attachments, caption, approvalNote, approvalNoteId } = this.state;
    const { offer } = subcampaign;
    const { id } = deliverable;
    const { approvalStatus: captionStatus } = caption;

    await this.showCaptionRePublishWarning();

    if (approvalNoteId) {
      await this.updateNote();
    }

    const addedAttachments = attachments.filter(attachment => typeof attachment.id === 'string');
    const deletedAttachments = postAttachments?.filter(postAttachment => {
      const isAttachmentDeleted = !attachments.find(attachment => attachment.id === postAttachment.id);

      return isAttachmentDeleted;
    }).map(attachment => attachment.id);

    // force update for deliverable ratings
    // used for the case when status was reset to pending but not yet saved
    // and we are trying to delete deliverable with freshly updated status
    const approvalAttachments = attachments
      .map(attachment => ({
        id: attachment.id,
        status: attachment.approvalStatus
      }))
      .concat(
        deletedAttachments.map(attachmentId => ({
          id: attachmentId,
          status: 'pending'
        }))
      )
      .filter(isPersistedAttachment);

    if (isForBrand) {
      await dispatchUpdateApprovalStatus(offer.id, deliverable.id, approvalAttachments, captionStatus);
    }

    const previouslySavedDeliverable = await dispatchGetPreviouslySavedDeliverable(offer.id, id);
    const wasCaptionUpdated = hasCaptionBeenUpdated(previouslySavedDeliverable, deliverable, isForBrand);

    if (!isForBrand && wasCaptionUpdated) {
      // @ts-ignore deletedAttachments will always have ids as number, as its extracted from BE data
      return this.triggerConfirmationModal(addedAttachments, deletedAttachments, previouslySavedDeliverable);
    }

    const updatedData = await this.handleDispatchUpdateContentApprovalData(
      offer.id,
      id,
      addedAttachments,
      // @ts-ignore deletedAttachments will always have ids as number, as its extracted from BE data
      deletedAttachments,
      caption,
      !approvalNoteId ? {
        text: approvalNote,
        authorType: isForBrand ? 'brand' : 'influencer'
      } : null
    );

    return this.finishSaveData(updatedData);
  };

  finishSaveData = async (data) => {
    const {
      deliverable,
      subcampaign,
      isForBrand,
      getIsPublishedToClientStatus,
      dispatchUpdateDeliverable,
      dispatchHideConfirmationModal
    } = this.props;

    const { offer } = subcampaign;
    const { id } = deliverable;

    if (!data) {
      dispatchHideConfirmationModal();
      return;
    }

    let updatedData = data;

    // add `isPublishedToClient` since it is lost during the update above
    const dataAssets = updatedData.assets.map(asset => ({
      ...asset,
      isPublishedToClient: getIsPublishedToClientStatus(asset)
    }));

    updatedData = {
      ...updatedData,
      assets: dataAssets,
      ...(isForBrand ? { caption: updatedData.caption } : null)
    };

    this.handleOnTrackDeliverableUploadEvent();
    this.handleModification(false, {
      approvalNote: '',
      isEditMode: !isForBrand,
      attachments: updatedData.assets || [],
      ...(!isNil(updatedData.caption) ? { caption: updatedData.caption } : null)
    });

    dispatchUpdateDeliverable(offer.id, id, updatedData, updatedData.contentApprovalTimeline);
    dispatchHideConfirmationModal();
  };

  updateNote = async () => {
    const { approvalNote, approvalNoteId } = this.state;
    const { deliverable, isForBrand, subcampaign, dispatchEditApprovalNote } = this.props;
    const { offer } = subcampaign;

    if (!approvalNoteId) {
      return;
    }

    await dispatchEditApprovalNote(offer.id, deliverable.id, approvalNoteId, approvalNote);

    this.handleModification(false, {
      approvalNote: '',
      approvalNoteId: null,
      isEditMode: !isForBrand
    });
  };

  sendApprovalWithNotes = async () => {
    // used when sending updates without adding new content
    const {
      isForBrand,
      subcampaign,
      deliverable,
      dispatchUpdateApprovalStatus,
      dispatchUpdateDeliverable,
      dispatchUpdateContentApprovalData
    } = this.props;

    const { attachments, caption, approvalNote, approvalNoteId } = this.state;
    const { offer } = subcampaign;
    const { id } = deliverable;
    const { text: captionText, approvalStatus: captionStatus } = caption;

    const approvalAttachments = attachments
      .map(attachment => ({
        id: attachment.id,
        status: attachment.approvalStatus
      }))
      .filter(isPersistedAttachment);

    const approvalStatusData = await dispatchUpdateApprovalStatus(offer.id, deliverable.id, approvalAttachments, captionStatus);

    if (approvalNoteId) {
      await this.updateNote();
    }

    const updatedData = await dispatchUpdateContentApprovalData(
      offer.id,
      id,
      [],
      [],
      caption,
      !approvalNoteId ? {
        text: approvalNote,
        authorType: isForBrand ? 'brand' : 'influencer'
      } : null
    );

    if (!updatedData) {
      return;
    }

    this.handleOnTrackDeliverableUploadEvent();
    this.handleModification(false, { approvalNote: '' });
    dispatchUpdateDeliverable(
      offer.id,
      id, {
        approvalNotes: updatedData.approvalNotes,
        assets: attachments,
        caption: { text: captionText, approvalStatus: captionStatus }
      },
      approvalStatusData || deliverable.contentApprovalTimeline
    );
  };

  getUpdatedCaptionStatus = (attachmentId?: number | null): DeliverableAttachmentApprovalStatus => {
    const { deliverable, clientApprovalData } = this.props;
    const { caption } = this.state;
    const { contentApproval } = deliverable;
    const { captionStatus = 'pending' } = clientApprovalData || {};
    const approvalStatus = contentApproval.caption.approvalStatus || APPROVAL_STATUS_PENDING;

    // don't use the client caption status if "apply client's feedback" is
    // triggered from the individual attachment context menu
    if (attachmentId) {
      return caption.approvalStatus;
    }

    return !isNil(captionStatus) && captionStatus !== APPROVAL_STATUS_PENDING
      ? captionStatus
      : approvalStatus;
  };

  getUpdatedAttachments = (attachmentId?: number | null): Array<DeliverableAttachment> => {
    const { clientApprovalData } = this.props;
    const { attachments } = this.state;
    const { items = [] } = clientApprovalData || {};

    // single attachment
    if (attachmentId) {
      const clientApprovalItem = items.find(i => i.assetId === attachmentId);

      return attachments.map(attachment => ({
        ...attachment,
        approvalStatus: clientApprovalItem?.assetId === attachment.id
          ? clientApprovalItem.status
          : attachment.approvalStatus
      }));
    }

    // all attachments
    return attachments.map(attachment => {
      const item = (items || []).find(i => i.assetId === attachment.id);

      return {
        ...attachment,
        approvalStatus: item?.status || attachment.approvalStatus
      };
    });
  };

  hasContentStatusBeenChanged(attachmentId?: number | null): boolean {
    const { attachments } = this.state;

    // single item
    if (attachmentId) {
      return attachments.some(attachment => (
        attachment.id === attachmentId && attachment.approvalStatus !== APPROVAL_STATUS_PENDING
      ));
    }

    // all items
    return attachments.some(attachment => (
      attachment.approvalStatus !== APPROVAL_STATUS_PENDING
    ));
  }

  handleSendApprovalNote = async () => {
    const { subcampaign, deliverable, isForBrand } = this.props;

    const { approvalNote, approvalNoteId } = this.state;
    const { offer } = subcampaign;
    const { id } = deliverable;

    const note = {
      text: approvalNote,
      authorType: (isForBrand ? 'brand' : 'influencer') as BrandInfluencer
    };

    if (!approvalNoteId) {
      const updatedData = await this.handleDispatchUpdateContentApprovalData(
        offer.id,
        id,
        [],
        [],
        { text: '', approvalStatus: 'not_required' },
        note
      );

      this.finishSaveData(updatedData);
    } else {
      await this.updateNote();
    }
  };

  handleCancelApprovalNote = () => {
    this.setState(prevState => ({
      ...prevState,
      approvalNote: '',
      approvalNoteId: null
    }));
  };

  handleApprovalStatusChanged = (
    updatedAttachments: Array<DeliverableAttachment>,
    updatedCaptionStatus: DeliverableAttachmentApprovalStatus
  ): void => {
    const { caption } = this.state;

    this.handleModification(true, {
      attachments: [...updatedAttachments],
      caption: {
        ...caption,
        approvalStatus: updatedCaptionStatus
      }
    });
  };

  handleApplyClientFeedback = (deliverable: Deliverable, attachmentId?: number | null): void => {
    if (this.hasContentStatusBeenChanged(attachmentId)) {
      this.handleApplyClientFeedbackModal(this.handleApplyClientFeedbackApply, attachmentId);
      return;
    }

    this.handleApplyClientFeedbackApply(attachmentId);
  };

  handleApplyClientFeedbackCaption = (): void => {
    const { deliverable } = this.props;
    const { contentApproval } = deliverable;
    const { caption } = this.state;
    const clientFeedbackCaptionStatus = this.getUpdatedCaptionStatus();

    const updatedCaption = {
      ...caption,
      approvalStatus: this.getUpdatedCaptionStatus()
    };

    const contentApprovalStatus = contentApproval.caption.approvalStatus;

    const shouldShowOverwriteModal = contentApprovalStatus !== APPROVAL_STATUS_PENDING
      && clientFeedbackCaptionStatus !== APPROVAL_STATUS_PENDING
      && contentApprovalStatus !== clientFeedbackCaptionStatus;

    if (shouldShowOverwriteModal) {
      this.handleApplyClientFeedbackModal(() => {
        this.handleModification(true, {
          caption: updatedCaption
        });
      });

      return;
    }

    this.handleModification(true, {
      caption: updatedCaption
    });
  };

  handleApplyClientFeedbackApply = (attachmentId?: number | null): void => {
    const updatedAttachments = this.getUpdatedAttachments(attachmentId);
    const updatedCaptionStatus = this.getUpdatedCaptionStatus(attachmentId);

    this.handleApprovalStatusChanged(updatedAttachments, updatedCaptionStatus);
  };

  handleApplyClientFeedbackModal(callback: ClientFeebackApplyCallback, attachmentId?: number | null): void {
    const { dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;

    dispatchShowConfirmationModal({
      title: __t(`${msgKey}.overwrite_feedback_modal.title`),
      message: __t(`${msgKey}.overwrite_feedback_modal.info`),
      confirmButtonLabel: __t(`${msgKey}.overwrite_feedback_modal.confirm_btn`),
      innerContent: (
        <div className="deliverable-content-approval-tab__modal-confirmation">
          {__t(`${msgKey}.overwrite_feedback_modal.confirmation`)}
        </div>
      ),
      onConfirm: () => {
        callback(attachmentId);
        dispatchHideConfirmationModal();
      },
      onCancel: () => dispatchHideConfirmationModal()
    });
  }

  handleCaptionChange = (value: string) => {
    const { caption } = this.state;

    this.handleModification(true, {
      caption: {
        text: value,
        approvalStatus: caption.approvalStatus
      }
    });
  };

  handleApprovalNoteDelete = (noteId: number) => {
    const {
      deliverable,
      subcampaign,
      dispatchEditApprovalNote,
      dispatchHideConfirmationModal,
      dispatchShowConfirmationModal
    } = this.props;
    const { offer } = subcampaign;

    dispatchShowConfirmationModal({
      title: __t(`${msgKey}.delete_note_modal.title`),
      message: __t(`${msgKey}.delete_note_modal.info`),
      confirmButtonLabel: __t(`${msgKey}.delete_note_modal.remove_btn`),
      onClose: () => dispatchHideConfirmationModal(),
      onCancel: () => dispatchHideConfirmationModal(),
      onConfirm: async () => {
        if (!noteId) {
          return;
        }

        dispatchHideConfirmationModal();
        await dispatchEditApprovalNote(offer.id, deliverable.id, noteId, '');
      },
      innerContent: this.renderInnerContent(__t(`${msgKey}.delete_note_modal.confirmation_message`))
    });
  };

  handleApprovalNoteEdit = (id: number, text: string) => {
    const { approvalNote } = this.state;
    const { dispatchHideConfirmationModal, dispatchShowConfirmationModal } = this.props;

    if (approvalNote) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.discard_unsaved_note_modal.title`),
        message: __t(`${msgKey}.discard_unsaved_note_modal.info`),
        confirmButtonLabel: __t(`${msgKey}.discard_unsaved_note_modal.discard_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onCancel: () => dispatchHideConfirmationModal(),
        onConfirm: () => {
          this.setState({
            approvalNote: text,
            approvalNoteId: id
          });

          dispatchHideConfirmationModal();
        },
        innerContent: this.renderInnerContent(__t(`${msgKey}.discard_unsaved_note_modal.confirmation_message`))
      });
    } else {
      this.setState({
        approvalNote: text,
        approvalNoteId: id,
        isModified: true
      });
    }
  };

  handleApprovalNoteChange = (value: string) => {
    this.handleModification(true, {
      approvalNote: value
    });
  };

  handleDownloadAttachments = (approvedOnly?: boolean | null) => {
    const { deliverable, subcampaign, dispatchDownloadAttachments } = this.props;
    const { campaignId, creatorProfile } = subcampaign;

    dispatchDownloadAttachments(campaignId, creatorProfile, deliverable, undefined, approvedOnly);
  };

  handleUpdateAttachments = (tempAttachments: Array<DeliverableAttachment>) => {
    this.handleModification(true, {});

    this.setState(prevState => ({
      ...prevState,
      attachments: [...prevState.attachments, ...tempAttachments]
    }));
  };

  handlAttachmentsApprovalStatusChange = (deliverableAttachment: DeliverableAttachment, approvalStatus: DeliverableAttachmentApprovalStatus) => {
    const { attachments } = this.state;

    const updatedAttachments = attachments.map(attachment => {
      if (attachment.id === deliverableAttachment.id) {
        return {
          ...attachment,
          approvalStatus
        };
      }

      return attachment;
    });

    this.handleModification(true, { attachments: [...updatedAttachments] });
  };

  resetApprovalStatus = (
    deliverableAttachments: Array<DeliverableAttachment>,
    resetAll?: boolean,
    resetCaption?: boolean) => {
    const { attachments, caption } = this.state;

    const updatedAttachments = attachments.map(attachment => {
      if (resetAll || deliverableAttachments[0]?.id === attachment.id) {
        return {
          ...attachment,
          approvalStatus: APPROVAL_STATUS_PENDING as DeliverableAttachmentApprovalStatus
        };
      }

      return attachment;
    });

    this.handleModification(true, {
      attachments: [...updatedAttachments],
      caption: {
        text: caption.text,
        approvalStatus: (resetAll || resetCaption) ? APPROVAL_STATUS_PENDING : caption.approvalStatus
      }
    });
  };

  handleResetApprovalStatus = (
    deliverableAttachments: Array<DeliverableAttachment>,
    resetAll?: boolean,
    resetCaption?: boolean) => {
    const { deliverable, dispatchHideConfirmationModal, dispatchShowConfirmationModal } = this.props;
    const { assets } = deliverable.contentApproval;
    const { text, approvalStatus } = deliverable.contentApproval?.caption || {};

    const isAttachmentsConfirmationRequired = deliverableAttachments.some(deliverableAttachment => {
      const originalAttachment = assets.find(asset => asset.id ===
        (asset.approvalStatus !== APPROVAL_STATUS_PENDING && deliverableAttachment.id));

      return !!originalAttachment;
    });

    const isCaptionConfirmationRequired = (resetAll || resetCaption) && text && approvalStatus != APPROVAL_STATUS_PENDING;

    if (isAttachmentsConfirmationRequired || isCaptionConfirmationRequired) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.reset_approval_modal.title`),
        message: __t(`${msgKey}.reset_approval_modal.info`),
        confirmButtonLabel: __t(`${msgKey}.reset_approval_modal.confirm_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onCancel: () => dispatchHideConfirmationModal(),
        onConfirm: () => {
          dispatchHideConfirmationModal();

          this.resetApprovalStatus(deliverableAttachments, resetAll, resetCaption);
        },
        innerContent: (
          <React.Fragment>
            <div className="deliverable-content-approval-tab__modal-info">
              {__t(`${msgKey}.reset_approval_modal.confirmation_message`)}
            </div>
            <div className="deliverable-content-approval-tab__modal-message">
              {__t(`${msgKey}.reset_approval_modal.message`)}
            </div>
          </React.Fragment>
        )
      });
    } else {
      this.resetApprovalStatus(deliverableAttachments, resetAll, resetCaption);
    }
  };

  handleDeleteAttachments = (deliverableAttachments: Array<DeliverableAttachment>) => {
    const { attachments } = this.state;

    const updatedAttachments = attachments.filter(
      attachment => !deliverableAttachments.find(deliverableAttachment => deliverableAttachment.id === attachment.id));

    this.handleModification(true, { attachments: [...updatedAttachments] });
  };

  handleDeleteOutfits = () => {
    const {
      deliverable,
      subcampaign,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal,
      dispatchDeleteOutfits
    } = this.props;

    dispatchShowConfirmationModal({
      title: __t(`${msgKey}.outfit_delete_modal.title`),
      message: this.renderDeleteModalMessage(),
      confirmButtonLabel: __t(`${msgKey}.outfit_delete_modal.confirm_btn`),
      onClose: () => dispatchHideConfirmationModal(),
      onCancel: () => dispatchHideConfirmationModal(),
      onConfirm: async () => {
        dispatchHideConfirmationModal();

        await dispatchDeleteOutfits(deliverable.id, subcampaign.offer.id);
      },
      innerContent: this.renderInnerContent(__t(`${msgKey}.outfit_delete_modal.confirmation_message`))
    });
  };

  shouldSave = () => {
    const { dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;
    const { isModified } = this.state;

    if (!isModified) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.no_changes_modal.title`),
        message: __t(`${msgKey}.no_changes_modal.message`),
        confirmButtonLabel: __t(`${msgKey}.no_changes_modal.confirm_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onConfirm: () => dispatchHideConfirmationModal()
      });

      return false;
    }

    return true;
  };

  handleSave = (shouldSendApproval?: boolean) => {
    if (!this.shouldSave()) {
      return;
    }

    const { deliverable, isForBrand, dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;
    const { attachments, caption, approvalNote, approvalNoteId, isEditMode } = this.state;
    const { captionApprovalRequired } = deliverable;
    const { text } = caption;
    const isEditingNote = Boolean(approvalNote && approvalNoteId);

    const { assets } = deliverable.contentApproval;
    const noContentProvided = !(attachments.length || (captionApprovalRequired && text));
    const isCaptionWithNoAttachments = !attachments.length && captionApprovalRequired && text;
    const isAttachmentsWithNoCaption = attachments.length && captionApprovalRequired && !text;
    const arePendingAttachmentsRemoved = attachments.length < assets.length
      && attachments.filter(attachment => typeof attachment.id === 'string').length === 0;

    if (isEditingNote && noContentProvided) {
      this.updateNote();
      return;
    }

    if (isEditingNote && shouldSendApproval) {
      this.sendApprovalWithNotes();
      return;
    }

    if (isEditingNote && isForBrand) {
      this.saveData();
      return;
    }


    if (!approvalNote && noContentProvided) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.add_content_modal.${isForBrand ? 'brand' : 'creator'}.title`),
        message: __t(`${msgKey}.add_content_modal.${isForBrand ? 'brand' : 'creator'}.message`),
        confirmButtonLabel: __t(`${msgKey}.add_content_modal.${isForBrand && !isEditMode ? 'edit_content' : 'continue_btn'}`),
        onClose: () => dispatchHideConfirmationModal(),
        onCancel: () => dispatchHideConfirmationModal(),
        onConfirm: () => {
          this.saveData();
          dispatchHideConfirmationModal();
        }
      });

      return;
    }

    if (approvalNote && noContentProvided && deliverable.deliverableType !== DELIVERABLE_TYPE_ZALANDO_OUTFIT) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.add_content_modal.${isForBrand ? 'brand' : 'creator'}.title`),
        message: __t(`${msgKey}.add_content_modal.${isForBrand ? 'brand' : 'creator'}.${isEditMode ? 'message' : 'message_with_hint'}`),
        confirmButtonLabel: __t(`${msgKey}.add_content_modal.send_the_note_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onCancel: () => dispatchHideConfirmationModal(),
        onConfirm: () => {
          dispatchHideConfirmationModal();

          if (shouldSendApproval) {
            this.sendApprovalWithNotes();
          } else {
            this.saveData();
          }
        },
        innerContent:
          this.renderInnerContent(__t(`${msgKey}.add_content_modal.${isForBrand ? 'brand' : 'creator'}.send_note`))
      });

      return;
    }

    if (shouldSendApproval) {
      this.sendApprovalWithNotes();

      return;
    }

    if (!isForBrand && arePendingAttachmentsRemoved) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.removal_pending_items_modal.title`),
        message: __t(`${msgKey}.removal_pending_items_modal.message`),
        confirmButtonLabel: __t(`${msgKey}.removal_pending_items_modal.remove_btn`),
        onClose: () => {
          dispatchHideConfirmationModal();
        },
        onCancel: () => {
          dispatchHideConfirmationModal();
        },
        innerContent: this.renderInnerContent(__t(`${msgKey}.removal_pending_items_modal.confirmation_message`)),
        onConfirm: () => this.saveData()
      });

      return;
    }

    if (!isForBrand && (isCaptionWithNoAttachments || isAttachmentsWithNoCaption)) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.add_content_modal.creator.title`),
        message: __t(`${msgKey}.add_content_modal.creator.${isCaptionWithNoAttachments ? 'no_attachment_warning' : 'no_caption_warning'}`),
        confirmButtonLabel: __t(`${msgKey}.add_content_modal.i_will_do_later_btn`),
        onClose: () => {
          dispatchHideConfirmationModal();
        },
        onCancel: () => {
          dispatchHideConfirmationModal();
        },
        onConfirm: () => this.saveData()
      });
    } else {
      this.saveData();
    }
  };

  handleCancelChanges = () => {
    const { deliverable, postAttachments, isForBrand, dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;
    const { isModified } = this.state;
    const { contentApproval } = deliverable;

    if (isModified) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.discard_changes_modal.title`),
        message: __t(`${msgKey}.discard_changes_modal.info`),
        confirmButtonLabel: __t(`${msgKey}.discard_changes_modal.discard_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onCancel: () => dispatchHideConfirmationModal(),
        onConfirm: () => {
          this.handleModification(false, {
            caption: contentApproval.caption,
            approvalNote: '',
            approvalNoteId: null,
            attachments: postAttachments,
            isEditMode: !isForBrand
          });

          dispatchHideConfirmationModal();
        },
        innerContent: this.renderInnerContent(__t(`${msgKey}.discard_changes_modal.confirmation_message`))
      });
    } else {
      this.setState(prevState => ({
        ...prevState,
        isEditMode: !isForBrand
      }));
    }
  };

  handleCaptionApprovalStatusChange = (status: DeliverableAttachmentApprovalStatus) => {
    const { caption } = this.state;

    this.handleModification(true, {
      caption: {
        text: caption.text,
        approvalStatus: status
      }
    });
  };

  handleEditMode = (isEditMode: boolean) => {
    this.setState(prevState => ({
      ...prevState,
      isEditMode
    }));
  };

  renderDeleteModalMessage = (): React.ReactElement => (
    <React.Fragment>
      <div>
        {__t(`${msgKey}.outfit_delete_modal.message_1`)}
      </div>
      <div>
        {__t(`${msgKey}.outfit_delete_modal.message_2`)}
      </div>
    </React.Fragment>
  );

  renderInnerContent = (message: string): React.ReactElement => (
    <div className="deliverable-content-approval-tab__modal-info">
      {message}
    </div>
  );

  render() {
    const {
      deliverable,
      isForBrand,
      isCcm,
      subcampaign,
      contentApprovalView,
      deliverablesWithCA2,
      setDeliverableModified,
      hasClientProvidedCaptionFeedback,
      onContentMoved,
      onOpenMoveContent,
      onOpenClientApproval,
      onSelectMoveOutfitToAnotherDeliverable
    } = this.props;

    const { isModified, isEditMode, caption, attachments, approvalNote, approvalNoteId } = this.state;
    const { contentApprovalTimeline, contentGoLiveTimeline, captionApprovalRequired, contentApproval, deliverableType } = deliverable;
    const { text: contentCaption, approvalStatus: captionStatus } = caption || {};
    const { approvalNotes } = contentApproval;
    const currentAuthorType = isForBrand ? 'brand' : 'influencer';
    const allowContentMove = deliverablesWithCA2.length > 1;
    const isZalandoOutfit = deliverable.deliverableType === DELIVERABLE_TYPE_ZALANDO_OUTFIT;
    const isEntertainmentVideo = deliverable.deliverableType === DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO;

    const contentApprovalDateRange = deliverableType === 'other'
      ? getDisplayDateRange(contentGoLiveTimeline.startDate, contentGoLiveTimeline.endDate)
      : getDisplayDateRange(contentApprovalTimeline.startDate, contentApprovalTimeline.endDate);

    if (contentApprovalView === 'client-approval') {
      return (
        <DeliverableClientApproval
          attachments={attachments}
          deliverable={deliverable}
          subcampaign={subcampaign}
          isForBrand={isForBrand}
          setDeliverableModified={setDeliverableModified}
          onOpenClientApproval={onOpenClientApproval}
        />
      );
    }

    if (contentApprovalView === 'move-content') {
      return isZalandoOutfit ? (
        <DeliverableMoveContentOutfits
          outfits={getOutfitsWithoutDraft(deliverable.outfits || [])}
          deliverable={deliverable}
          subcampaign={subcampaign}
          deliverablesWithCA2={deliverablesWithCA2}
          setDeliverableModified={setDeliverableModified}
          onOpenMoveContent={onOpenMoveContent}
          onSave={onContentMoved}
        />
      ) : (
        <DeliverableMoveContentAttachments
          attachments={attachments}
          deliverable={deliverable}
          subcampaign={subcampaign}
          deliverablesWithCA2={deliverablesWithCA2}
          setDeliverableModified={setDeliverableModified}
          onOpenMoveContent={onOpenMoveContent}
          onSave={onContentMoved}
        />
      );
    }

    return (
      <div className="deliverable-content-approval-tab">
        {
          isModified && (
            <PromptExit
              type="page"
              message={__t(`${msgKey}.discard_changes_modal.info`)}
              title={__t(`${msgKey}.discard_changes_modal.title`)}
              innerContent={ReactDOMServer.renderToString(
                <div className="deliverable-content-approval-tab__modal-info">
                  {__t(`${msgKey}.discard_changes_modal.confirmation_message`)}
                </div>
              )}
            />
          )
        }
        <div>
          <div className="deliverable-content-approval-tab__date-section">
            {`${__t(`${msgKey}.labels.content_approval_date`)}:
            ${contentApprovalDateRange}`}
          </div>
          <div className="deliverable-content-approval-tab__subheadline">
            {__t(`${msgKey}.content_approval_subheadline.${isForBrand ? 'brand' : 'influencer'}.${
              // eslint-disable-next-line no-nested-ternary
              isZalandoOutfit
                ? 'outfit'
                : isEntertainmentVideo
                  ? 'entertainment_video'
                  : 'content'
            }`)}
            {
              (isZalandoOutfit || isEntertainmentVideo) && !isForBrand && (
                <ActionLink
                  openInNewTab
                  link="https://apps.apple.com/us/app/collabary-influencer-app/id1252570939"
                  color="primary"
                >
                  Collabary Mobile App
                </ActionLink>
              )
            }
          </div>
        </div>
        <div className="deliverable-content-approval-tab__main-container">
          {
            // eslint-disable-next-line no-nested-ternary
            isZalandoOutfit && deliverable.outfits ? (
              <DeliverableOutfitContentForReview
                campaign={subcampaign.campaign}
                deliverableId={deliverable.id}
                creatorWmcLink={deliverable.creatorWmcShortLink || null}
                offerId={subcampaign.offer.id}
                isForBrand={isForBrand}
                influencerProfile={subcampaign.creatorProfile}
                outfits={deliverable.outfits}
                onSelectMoveOutfitToAnotherDeliverable={onSelectMoveOutfitToAnotherDeliverable}
              />
            ) : isEntertainmentVideo && deliverable.entertainmentVideos ?
              (
                <DeliverableEntertainmentVideoForReview
                  deliverableId={deliverable.id}
                  offerId={subcampaign.offer.id}
                  entertainmentVideos={deliverable.entertainmentVideos}
                  isForBrand={isForBrand}
                  influencerProfile={subcampaign.creatorProfile}
                />
              ) : (
                <DeliverableContentForApproval
                  isForBrand={isForBrand}
                  isEditMode={isEditMode}
                  deliverable={deliverable}
                  subcampaign={subcampaign}
                  attachments={attachments}
                  allowContentMove={allowContentMove}
                  onOpenMoveContent={onOpenMoveContent}
                  onEditMode={this.handleEditMode}
                  onUpdate={this.handleUpdateAttachments}
                  onApprovalStatusChanged={this.handlAttachmentsApprovalStatusChange}
                  onApplyClientFeedback={this.handleApplyClientFeedback}
                  onResetApprovalStatus={this.handleResetApprovalStatus}
                  onDeleteAttachments={this.handleDeleteAttachments}
                  onOpenClientApproval={onOpenClientApproval}
                />
              )
          }
          {
            captionApprovalRequired && (
              <DeliverableCaptionApproval
                caption={contentCaption || ''}
                isForBrand={isForBrand}
                isEditMode={isEditMode}
                deliverable={deliverable}
                status={captionStatus || ''}
                hasClientProvidedFeedback={hasClientProvidedCaptionFeedback()}
                onSetCaption={this.handleCaptionChange}
                onEditMode={this.handleEditMode}
                onResetCaptionStatus={() => this.handleResetApprovalStatus([], false, true)}
                onCaptionStatusChange={this.handleCaptionApprovalStatusChange}
                onApplyClientFeedback={this.handleApplyClientFeedbackCaption}
              />
            )
          }
        </div>
        <DeliverableApprovalNote
          noteValue={approvalNote}
          noteId={approvalNoteId}
          notes={approvalNotes.map(note => ({ ...note, isNew: note.isNew && currentAuthorType !== note.author.type }))}
          uniqueId={deliverable.id}
          isForBrand={isForBrand}
          onNewNoteChange={this.handleApprovalNoteChange}
          onNoteEdit={this.handleApprovalNoteEdit}
          onNoteDelete={this.handleApprovalNoteDelete}
          onSend={this.handleSendApprovalNote}
          onCancel={this.handleCancelApprovalNote}
          isZalandoOutfit={isZalandoOutfit}
          isEntertainmentVideo={isEntertainmentVideo}
        />
        <div className="deliverable-content-approval-tab__section deliverable-content-approval-tab__actions">
          <DeliverableTabFooter
            key={`deliverable-content-approval-tab__actions--${deliverable.id}`}
            deliverable={deliverable}
            isEditMode={isEditMode}
            attachments={attachments}
            isForBrand={isForBrand}
            isModified={isModified}
            isCcm={isCcm}
            captionStatus={captionStatus || ''}
            allowContentMove={allowContentMove}
            hasClientProvidedCaptionFeedback={hasClientProvidedCaptionFeedback()}
            onSave={() => this.handleSave(false)}
            onSend={() => this.handleSave(true)}
            onDeleteAttachments={this.handleDeleteAttachments}
            onCancelChanges={this.handleCancelChanges}
            onOpenMoveContent={onOpenMoveContent}
            onReleaseToClient={onOpenClientApproval}
            onDownloadAttachments={this.handleDownloadAttachments}
            onEditModeToggle={this.handleEditMode}
            onApplyClientFeedback={this.handleApplyClientFeedback}
            onDeleteOutfits={this.handleDeleteOutfits}
            onResetApprovalStatus={(deliverableAttachments) =>
              this.handleResetApprovalStatus(deliverableAttachments, true)}
          />
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDownloadAttachments: (
    campaignId: number,
    creatorProfile: SubcampaignInfluencerProfile | undefined | null,
    deliverable: Deliverable,
    attachmentType?: string | null,
    approvedOnly?: boolean | null) =>
    dispatch(downloadDeliverableAttachments(campaignId, creatorProfile, deliverable, attachmentType, approvedOnly)),

  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchUpdateContentApprovalData: (
    offerId: number,
    deliverableId: number,
    addAttachments: Array<DeliverableAttachment>,
    deleteAttachments: Array<number>,
    caption: DeliverableCaption,
    note: Note | null
  ) => (
    dispatch(updateContentApprovalData(
      offerId,
      deliverableId,
      addAttachments,
      deleteAttachments,
      caption,
      note
    ))
  ),
  dispatchMarkApprovalNoteAsRead: (offerId: number, deliverableId: number, ids: Array<number>, readerType: BrandInfluencer) => (
    dispatch(markApprovalNoteAsRead(offerId, deliverableId, ids, readerType))
  ),
  dispatchEditApprovalNote: (offerId: number, deliverableId: number, noteId: number, noteValue: string) => (
    dispatch(editApprovalNote(offerId, deliverableId, noteId, noteValue))
  ),
  dispatchUpdateApprovalStatus: (
    offerId: number,
    deliverableId: number,
    attachments: Array<DeliverablePersistedAttachment>,
    captionStatus?: DeliverableAttachmentApprovalStatus
  ) => (
    dispatch(updateApprovalStatus(offerId, deliverableId, attachments, captionStatus))
  ),
  dispatchUpdateDeliverable: (
    offerId: number,
    deliverableId: number,
    contentApproval: DeliverableContentApproval,
    contentApprovalTimeline: DeliverableGenericTimeline
  ) => (
    dispatch(updateContentApproval(offerId, deliverableId, contentApproval, contentApprovalTimeline))
  ),
  dispatchGetPreviouslySavedDeliverable: (
    offerId: number,
    deliverableId: number
  ) => (
    dispatch(getPreviouslySavedDeliverable(offerId, deliverableId))
  ),
  dispatchDeleteOutfits: (deliverableId: number, offerId: number) => dispatch(deleteDeliverableOutfits(deliverableId, offerId))
});

export default connect(null, mapDispatchToProps)(DeliverableContentApprovalTab);
